import { ACTIVE_APPOINTMENT_STATUSES, type AppointmentStatus } from '@app/shared';
import type { MyAppointmentDto } from '../api/types';
import type { UiKey } from '../i18n/dict';

/**
 * Разбор выдачи «Моих записей» на секции экрана (ТЗ §14.1).
 *
 * Признак «прошло» берётся ИЗ ОТВЕТА (`isPast`), а не считается здесь сравнением с
 * `Date.now()`. Сервер посчитал его по тому же `now`, по которому делал выборку, и его
 * ответ внутренне непротиворечив. Наш `now` отстаёт на время передачи, часы телефона
 * могут быть сбиты на часы, а пояс браузера вообще не пояс организации (AGENTS.md,
 * правило 1) — любое из трёх дало бы запись, которой нет ни в одной секции, или
 * дубль в обеих.
 */

export interface SplitAppointments {
  /** Предстоящие: ближайшая первой — единственная, с которой клиент что-то делает. */
  readonly upcoming: readonly MyAppointmentDto[];
  /** Прошедшие: последняя первой. */
  readonly past: readonly MyAppointmentDto[];
}

function byStartAsc(a: MyAppointmentDto, b: MyAppointmentDto): number {
  return Date.parse(a.startsAt) - Date.parse(b.startsAt);
}

export function splitAppointments(items: readonly MyAppointmentDto[]): SplitAppointments {
  const upcoming: MyAppointmentDto[] = [];
  const past: MyAppointmentDto[] = [];

  for (const item of items) {
    (item.isPast ? past : upcoming).push(item);
  }

  // Порядок восстанавливается явно, а не наследуется от сервера: он уже отдаёт нужный,
  // но экран не должен ломаться от того, что кто-то поменяет там `orderBy`.
  upcoming.sort(byStartAsc);
  past.sort((a, b) => byStartAsc(b, a));

  return { upcoming, past };
}

/**
 * Отменённые записи из выдачи НЕ выкидываются.
 *
 * Спрятав их, мы спрятали бы тот факт, что визит отменил бизнес (ТЗ §11.4): клиент пришёл
 * бы к закрытой двери. Карточка остаётся, но действий на ней нет.
 */
export function isActive(status: AppointmentStatus): boolean {
  return ACTIVE_APPOINTMENT_STATUSES.includes(status);
}

/** Отменить можно только живую предстоящую запись. */
export function canCancel(item: MyAppointmentDto): boolean {
  return !item.isPast && isActive(item.status);
}

/** Подтверждает клиент запись в статусе CREATED (ТЗ §6.2, §11.2). Повторно — нечего. */
export function canConfirm(item: MyAppointmentDto): boolean {
  return !item.isPast && item.status === 'CREATED';
}

/**
 * Число неявок в видимой истории — источник баннера-предупреждения (ТЗ §14.2).
 *
 * Считается по статусам записей, а не по отдельному полю: поля «сколько у меня неявок»
 * в клиентском API нет (см. отчёт). Отсюда ограничение, о котором надо помнить: выдача
 * «Моих записей» покрывает окно истории сервера (по умолчанию 90 дней), поэтому счётчик
 * НЕ равен глобальному `no_show_count_cached` и служит только порогом показа баннера.
 * Санкции считает бэкенд по `no_show_events` (AGENTS.md, правило 6), и здесь они
 * не воспроизводятся.
 */
export function countNoShows(items: readonly MyAppointmentDto[]): number {
  return items.filter((item) => item.status === 'NO_SHOW').length;
}

/** Цвет бейджа статуса. Отдельно от подписи: подпись — i18n, цвет — оформление. */
export type StatusTone = 'new' | 'ok' | 'done' | 'cancelled' | 'noshow';

export function statusTone(status: AppointmentStatus): StatusTone {
  switch (status) {
    case 'CREATED':
      return 'new';
    case 'CONFIRMED':
      return 'ok';
    case 'COMPLETED':
      return 'done';
    case 'CANCELLED':
      return 'cancelled';
    case 'NO_SHOW':
      return 'noshow';
  }
}

/** Подпись статуса. Ключ i18n, а не текст. */
export function statusTagKey(status: AppointmentStatus): UiKey {
  switch (status) {
    case 'CREATED':
      return 'tagNew';
    case 'CONFIRMED':
      return 'tagOk';
    case 'COMPLETED':
      return 'tagDone';
    case 'CANCELLED':
      return 'tagCancelled';
    case 'NO_SHOW':
      return 'tagNoShow';
  }
}
